/** @jsxImportSource @emotion/react */
import { FormProvider, useForm } from "react-hook-form";
import { Modal } from "./Modal";
import { StyledForm } from "./StyledForm";
import {
  FileUploadFormat,
  FileUploadInput,
} from "./forms/inputs/FileUploadInput";
import { ButtonAction } from "./NewButtons";
import { css } from "@emotion/react";
import { ActionSpinner } from "./ActionSpinner";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  multipleFiles?: boolean;
  accept?: FileUploadFormat;
  label?: string;
  onSubmit: (files: File[]) => Promise<void>;
};

const additionalFormStyles = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const validationSchema = yup.object({
  files: yup.array().min(1, "Please select at least one file"),
});

export const AddFileModal = ({
  isOpen,
  onClose,
  multipleFiles,
  accept,
  label,
  onSubmit,
}: Props) => {
  const methods = useForm<{ files: File[] }>({
    defaultValues: {
      files: [],
    },
    resolver: yupResolver(validationSchema),
  });

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  const handleSubmit = async (data: { files: File[] }) => {
    await onSubmit(data.files);
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} header="Upload file">
      <FormProvider {...methods}>
        <StyledForm css={additionalFormStyles}>
          <FileUploadInput
            name="files"
            accept={accept}
            multiple={multipleFiles}
            label={label}
          />
          <ButtonAction
            onClick={methods.handleSubmit(handleSubmit)}
            type="button"
            disabled={methods.formState.isSubmitting}
            width="100%"
          >
            {methods.formState.isSubmitting ? <ActionSpinner /> : "Use file"}
          </ButtonAction>
        </StyledForm>
      </FormProvider>
    </Modal>
  );
};
